/** @import {Account} from '../../contexts/CustomerDataProviderTypes' */
/** @import {Instruction} from './MaturityOptionsTypes' */
/** @import {OtherAccount} from './PayawayDetailsConfirmationTypes' */

import React, {useContext, useEffect, useState} from 'react';
import './maturityInstructionForm.scss';
import {customerDataContext} from '../../contexts/CustomerDataProvider';
import RollOverSummary from "./RollOverSummary";
import '../SharedComponents/text-styles.scss'
import CloseButton from "../SharedComponents/CloseButton3";
import Loading from "../Loading/Loading";
import MaturityOptionsStep from "./MaturityOptionsStep";
import ConfirmationStep from "./ConfirmationStep";
import PayawayDetailsConfirmation from "./PayawayDetailsConfirmation"
import {useNavigate} from "react-router";
import logoImg from "../../assets/images/investec-logo.svg";
import {productTypes} from "../../utils/productHelpers";
import withFeatureFlags from "../FeatureFlags/withFeatureFlags";
import PartialRollOverSummary from "./PartialRenewal/PartialRenewalForm/PartialRollOverSummary/PartialRollOverSummary";
import {useUnifiedAnalytics} from "../../contexts/AnalyticsWrapper";
/** @import {MaturityInstructionFormProps} from './MaturityInstructionFormTypes' */

function MaturityInstructionForm(/** @type {MaturityInstructionFormProps} */{featureEnabled}) {

    const path = window.location.pathname.split('/');

    const analytics = useUnifiedAnalytics();

    const [step, setStep] = useState("maturityOptions");
    const [selectedAccount, setSelectedAccount] = useState(/** @type {OtherAccount | undefined} */ (undefined));
    const [maturingAccount, setMaturingAccount] = useState(/** @type {Account | undefined} */ (undefined));
    const [uninstructedMaturingAccounts, setUninstructedMaturingAccounts] = useState(/** @type {Account[] | undefined} */ (undefined));
    const [instruction, setInstruction] = useState(/** @type {Instruction | null} */ (null));
    const navigate = useNavigate();

    const data = useContext(customerDataContext)?.data;

    useEffect(() => {
        if (data) {
            setUninstructedMaturingAccounts(data.customer.accounts.filter(a => (a.maturity?.maturityInstructionWindowStatus === "InstructionOpen" && !a.maturity.maturityInstruction)))
        }
    }, [data]);

    useEffect(()=>{
        if (data) {
            //if there is an account ID in the url, set the maturing account to that, if not, select the first account
            // with no instruction
            const accountIdFromUrl = data.customer.accounts.filter(account => account.id === path[path.length - 1])[0] && data.customer.accounts.filter(account => account.id === path[path.length - 1])[0].id
            updateMaturingAccountById(accountIdFromUrl
                ? accountIdFromUrl
                : (uninstructedMaturingAccounts && uninstructedMaturingAccounts.length > 0 ? uninstructedMaturingAccounts[0].id : '')
            )
        }
    },[uninstructedMaturingAccounts])

    const updateMaturingAccountById = (/** @type {string} */ accountId) => {
        setMaturingAccount(data?.customer.accounts.filter(account => account.id === accountId)[0])
    }

    const returnToDashboard = () => {
        window.location.href = "/"
    }

    const incrementStep = (/** @type {Instruction | null} */ newInstruction = null) => {
        //After each step is complete, this method determines which step will be displayed next.
        switch (step) {
            case 'maturityOptions': {
                if (newInstruction && newInstruction?.name?.includes('Rollover')) {
                    const nextStep = 'partialRollOverSummary';
                    return setStep(nextStep);
                }
                return setStep('payawayDetailsConfirmation');
            }
            case 'partialRollOverSummary':
                return setStep('confirmation');
            case 'payawayDetailsConfirmation':
                returnToDashboard();
        }
    }

    const handleStepCompleted = (/** @type {Instruction | null} */ newInstruction = null) => {
        if (newInstruction) {
            setInstruction(newInstruction)
            //The set instruction hook takes some time to update the instruction state, so we force increment step to use the correct value by passing it:
            incrementStep(newInstruction)
        }
        else {
            incrementStep()
        }
    }

    /** @returns {OtherAccount} */
    const getLinkedAccount = () => {
        return {
            accountNumber: data?.customer.accounts[0].linkedAccountNumber || "",
            sortCode: data?.customer.accounts[0].linkedAccountSortCode || "",
            id: "00000000-0000-0000-0000-000000000000", //empty guid
            product: {
                name: "Linked Account",
                type: "linked"
            }
        }
    }

    /** @returns {OtherAccount[] | undefined} */
    const getOtherAccounts = () => {
        const otherAccounts = data?.customer.accounts
            .filter(a => ((a.product.productType === productTypes.EasyAccess || a.product.productType === productTypes.Notice) && a.accountStatus !== "Closed"))
            .map(a => ({
                accountNumber: a.accountNumber,
                sortCode: a.sortCode,
                id: a.id,
                balance: a.balance,
                product: {
                    name: a.product.name,
                    type: a.product.productType,
                    maxWithdrawalLimit: a.product.maxWithdrawalLimit
                }
            })) ?? []

        const allAccounts = [...otherAccounts, getLinkedAccount()]

        return allAccounts;
    }

    const renderStep = (/** @type {Account | undefined} */ account) => {
        if (account) {
            switch (step){
                case 'maturityOptions' : {
                    return <MaturityOptionsStep
                        maturingAccount={account}
                        instruction={instruction}
                        uninstructedMaturingAccounts={uninstructedMaturingAccounts}
                        setMaturingAccount={setMaturingAccount}
                        onStepComplete={(i) => handleStepCompleted(i)}
                        featureEnabled={featureEnabled}
                        accountList={getOtherAccounts()}
                    />
                }
                case "rollOverSummary": {
                    return <RollOverSummary
                        account={account}
                        instruction={instruction}
                        mobileNumber={data?.customer.mobileNumber}
                        onClickBack={() => setStep("maturityOptions")}
                        selectedAccount={selectedAccount}
                        multipleMaturingAccounts={uninstructedMaturingAccounts && uninstructedMaturingAccounts.length > 1}
                        onStepComplete={() => incrementStep()}
                    />}
                case "partialRollOverSummary": {
                    return <PartialRollOverSummary
                        account={account}
                        instruction={instruction}
                        mobileNumber={data?.customer.mobileNumber}
                        onClickBack={() => setStep("maturityOptions")}
                        selectedAccount={selectedAccount}
                        multipleMaturingAccounts={uninstructedMaturingAccounts && uninstructedMaturingAccounts.length > 1}
                        onStepComplete={() => incrementStep()}
                        accountList={getOtherAccounts()}
                    />}
                case "payawayDetailsConfirmation": {

                    var previousDestinationAccount = getOtherAccounts()?.filter(a => a.id === maturingAccount?.maturity?.destinationAccountId)[0]
                    const destinationAccount = previousDestinationAccount
                        ? previousDestinationAccount
                        : getLinkedAccount()
                    return <PayawayDetailsConfirmation
                        accountList={getOtherAccounts()}
                        maturingAccount={maturingAccount}
                        onStepComplete={handleStepCompleted}
                        selectedAccount={destinationAccount}
                        setSelectedAccount={setSelectedAccount}
                        onStepCancel={() => setStep("maturityOptions")}
                        multipleMaturingAccounts={uninstructedMaturingAccounts && uninstructedMaturingAccounts.length > 1}
                        customerMobileNumber={data?.customer.mobileNumber}
                        featureEnabled={featureEnabled}
                    />
                }
                case "confirmation": {
                    return <ConfirmationStep
                        account={account}
                        multipleMaturingAccounts={Boolean(uninstructedMaturingAccounts && uninstructedMaturingAccounts.length > 1)}
                        instruction={instruction} />
                }
                default: {
                    return <div>Loading...</div>
                }
            }
        } else {
            return <div>Loading...</div>
        }
    };

    const goBack = () => {
        if (maturingAccount?.maturity?.maturityInstruction){
            //If an instruction has already been given
            analytics.trackAnalyticsClickEvent(
                "close without making changes",
                document.referrer,
                "bottom",
                maturingAccount?.product.code,
                "maturity",
                {},
                "link click",
                "navigation|close|maturity",
                "maturity"
            );

        }
        navigate(-1);
    }

    if (data) { //need to wait until data is loaded before doing any of this stuff
        return <>
            <div className={"maturity3__flex-row"}>
                <img src={logoImg} alt={"Investec"}/>
                <CloseButton onClick={goBack}/>
            </div>
            <div className={"text__navy maturity3 maturity3-form-container"}>
                {renderStep(maturingAccount)}
            </div>
        </>
    } else {
        return <Loading message="We’re loading your options..."/>;
    }
};

export default withFeatureFlags(MaturityInstructionForm);

